import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NoteInput, Student, StudentInput } from '@td/shared';
import { get, post, patch, del, ApiRequestError } from '../lib/api';

const KEY = ['students'];

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: KEY });
}

export function useStudents() {
  return useQuery({ queryKey: KEY, queryFn: () => get<Student[]>('/students') });
}

/**
 * Everyone, including the years already put away.
 *
 * A separate query rather than a flag on the one above, because almost
 * nothing wants past cohorts — the seating chart, the numbering and the data
 * grid all mean "the class I have now". Only the roster's Archived view asks
 * for the rest.
 */
/** One archived year's class, exactly as it was left. */
export function useYearStudents(schoolYear: string | null) {
  return useQuery({
    queryKey: [...KEY, 'year', schoolYear ?? 'none'],
    queryFn: () => get<Student[]>(`/students?schoolYear=${encodeURIComponent(schoolYear ?? '')}`),
    enabled: !!schoolYear,
  });
}

export function useArchivedStudents() {
  return useQuery({
    queryKey: [...KEY, 'archived'],
    queryFn: () => get<Student[]>('/students?archived=true'),
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StudentInput) => post<Student>('/students', input),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<StudentInput> & { id: string; number?: number }) =>
      patch<Student>(`/students/${id}`, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/students/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useRenumberStudents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<Student[]>('/students/renumber'),
    onSuccess: (students) => qc.setQueryData(KEY, students),
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: NoteInput & { id: string }) =>
      post<Student>(`/students/${id}/notes`, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      noteId,
      ...input
    }: {
      id: string;
      noteId: string;
      type?: NoteInput['type'];
      body?: string;
      needsFollowUp?: boolean;
      followedUp?: boolean;
    }) => patch<Student>(`/students/${id}/notes/${noteId}`, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, noteId }: { id: string; noteId: string }) =>
      del<Student>(`/students/${id}/notes/${noteId}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useUploadStudentPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: Blob }) => {
      const form = new FormData();
      form.append('image', file, 'photo.webp');
      const res = await fetch(`/api/students/${id}/photo`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiRequestError(res.status, body?.error ?? 'That photo could not be saved');
      }
      return (await res.json()) as Student;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteStudentPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<Student>(`/students/${id}/photo`),
    onSuccess: () => invalidate(qc),
  });
}

/** Photo URL, versioned so replacing a photo busts the browser cache. */
export function studentPhotoUrl(s: Student): string {
  return `/api/students/${s.id}/photo?v=${s.photoVersion}`;
}
